import { useEffect, useState } from "react";
import { useParticipants, useRoomContext } from "@livekit/components-react";
import type { Participant, Room } from "livekit-client";
import { ParticipantEvent, RoomEvent } from "livekit-client";
import type { ParticipantMetadata } from "../types";
import { ACTION_TYPES, parseParticipantMetadata } from "./helpers";
import { useLivekitLocalParticipant } from "./useLivekitLocalParticipant";
import { useLivekitPublishMetadata } from "./useLivekitPublishMetadata";

const useListenParticipantMetadata = (
  participant: Participant,
  setParticipantMetadata: (metadata: ParticipantMetadata) => void,
  setMetadataCache: (metadata: ParticipantMetadata) => void,
) => {
  useEffect(() => {
    const onMetadataChanged = (prev?: string): void => {
      // The isHandRaised metadata param is only streamed but this is not persisted.
      // More info here:
      //  - https://docs.livekit.io/client/data-messages/#data-messages
      //  - https://github.com/livekit/client-sdk-js/issues/148
      // This block avoids override the isHandRaised param with the persisted metadata
      const prevMetadata = JSON.parse(prev || "{}") as ParticipantMetadata;
      const currMetadata = JSON.parse(
        participant.metadata || "{}",
      ) as ParticipantMetadata;
      if (!("isHandRaised" in currMetadata)) {
        void setParticipantMetadata({
          ...currMetadata,
          isHandRaised: prevMetadata.isHandRaised,
        });
        return;
      }

      if (participant.metadata) {
        setMetadataCache(parseParticipantMetadata(participant));
      }
    };

    if (participant) {
      participant.on(
        ParticipantEvent.ParticipantMetadataChanged,
        onMetadataChanged,
      );
    }

    return () => {
      if (participant) {
        participant.off(
          ParticipantEvent.ParticipantMetadataChanged,
          onMetadataChanged,
        );
      }
    };
  }, [participant]);
};

const useListenRoomData = (
  room: Room,
  participant: Participant,
  metadata: ParticipantMetadata | null,
  setParticipantMetadata: (metadata: ParticipantMetadata) => void,
) => {
  useEffect(() => {
    const decoder = new TextDecoder();
    const onDataReceived = (data: Uint8Array): void => {
      try {
        const strData = decoder.decode(data);
        const action = JSON.parse(strData);
        if (action?.type === ACTION_TYPES.PUBLISH_METADATA && participant) {
          const participantId = action.payload?.participantId;
          const newMetadata = action.payload?.metadata as ParticipantMetadata;
          if (newMetadata && participant?.sid === participantId) {
            void setParticipantMetadata({ ...metadata, ...newMetadata });
          }
        }
      } catch (err: any) {
        console.error("Could not decode received data - ", err.message);
      }
    };

    if (room) {
      room.on(RoomEvent.DataReceived, onDataReceived);
    }

    return () => {
      if (room) {
        room.off(RoomEvent.DataReceived, onDataReceived);
      }
    };
  }, [metadata, participant, room]);
};

export const useLivekitPeerMetadata = (peerId: string) => {
  const room = useRoomContext();
  const participants = useParticipants();
  const localParticipant = useLivekitLocalParticipant();
  const participant =
    participants?.find(participant => participant.sid === peerId) ||
    localParticipant;
  const [metadata, setMetadata] = useState<ParticipantMetadata | null>(
    participant ? parseParticipantMetadata(participant) : null,
  );
  const publishMetadata = useLivekitPublishMetadata(participant);

  const setParticipantMetadata = (newMetadata: ParticipantMetadata) => {
    void publishMetadata(newMetadata);
  };

  useListenParticipantMetadata(
    participant,
    setParticipantMetadata,
    setMetadata,
  );

  useListenRoomData(room, participant, metadata, setParticipantMetadata);

  return {
    metadata,
    setMetadata: setParticipantMetadata,
  };
};
